import { ClipboardItem, write as clipboardWrite } from 'clipboard-polyfill';
import { MessageType } from 'modules/graphql';
import { SmsSender } from 'modules/sendingPlatforms/types';
import { addNegativeToast } from 'modules/toastContainer';
import { useMemo, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Edit16 } from 'ui/icons';
import { Button, BUTTON_VARIANTS } from 'ui/button';
import { Label } from 'ui/theme';
import { displayContent } from 'modules/templateEditor/common';
import { useSmsSmartOptInContext } from './contexts/SmsSmartOptInContext';
import {
  CopyButton,
  CopyContent,
  DetailFieldsWrapper,
  DetailsContainer,
  DetailsHeaderEditLabel,
  DetailsHeaderWrapper,
  ExpandableContainer,
  Header,
  InfoText,
  NeutralLink,
  StyledCaption,
  StyledFormField,
  StyledNeutralBanner,
} from './SmsSmartOptIn.styled';
import { SmsSmartOptInPreview } from './SmsSmartOptInPreview';

const {
  btnSmsSmartOptInDetailsCopy,
  btnSmsSmartOptInDetailsEdit,
  labelSmsSmartOptInDetailsDisclaimer,
  labelSmsSmartOptInDetailsLink,
  labelSmsSmartOptInFormBrandName,
  labelSmsSmartOptInFormMessageType,
  labelSmsSmartOptInFormPrivacyLink,
  labelSmsSmartOptInFormRedirectLink,
  labelSmsSmartOptInFormSender,
  labelSmsSmartOptInFormTermsLink,
  strSmsSmartOptInDetailsDescriptionDefault,
  strSmsSmartOptInDetailsDisclaimer,
  strSmsSmartOptInDetailsDisclaimerHideInfoText,
  strSmsSmartOptInDetailsDisclaimerPrivacyText,
  strSmsSmartOptInDetailsDisclaimerShowInfoText,
  strSmsSmartOptInDetailsDisclaimerTermsText,
  strSmsSmartOptInDetailsHeaderNeutralBannerBody,
  strSmsSmartOptInDetailsHeaderNeutralBannerLink,
  strSmsSmartOptInDetailsHeaderNeutralBannerTitle,
  strSmsSmartOptInDocumentationLinkUrl,
  strUnsetText,
} = displayContent;

const TIMEOUT_MS = 3000; // 3 seconds
const COPY_LINK_BUTTON_ID = 'sms-smart-opt-in-button-copy-link';
const COPY_DISCLAIMER_BUTTON_ID = 'sms-smart-opt-in-button-copy-disclaimer';

export const SmsSmartOptInDetails = () => {
  const {
    assetDetails: selectedLink,
    smsSenders,
    messageTypes,
    setIsEditing,
  } = useSmsSmartOptInContext();
  const [shouldShowBanner, setShouldShowBanner] = useState(true);
  const [shouldShowFullDisclaimer, setShouldShowFullDisclaimer] = useState(false);
  const smartLinkTextRef = useRef<HTMLSpanElement>(null);
  const disclaimerTextRef = useRef<HTMLDivElement>(null);

  const smsSender: SmsSender | undefined = useMemo(
    () => smsSenders.find((sender) => sender.id === selectedLink.smsSenderId),
    [selectedLink.smsSenderId, smsSenders],
  );
  const messageType: MessageType | undefined = useMemo(
    () => messageTypes.find((type) => type.id === selectedLink.messageTypeId),
    [selectedLink.messageTypeId, messageTypes],
  );
  const dispatch = useDispatch();

  const getDisclaimerText = () =>
    strSmsSmartOptInDetailsDisclaimer.replace('{{brandName}}', selectedLink.brandName as any); // TODO: fix the type for `brandName`

  const asLink = (linkText: string, linkUrl: string | undefined) => {
    if (linkUrl) {
      return (
        <a href={linkUrl} target="_blank">
          <InfoText isUnderlined>{linkText}</InfoText>
        </a>
      );
    }
    return linkText;
  };

  const timeouts: { [key: string]: ReturnType<typeof setTimeout> } = {};
  const handleCopyToClipboard = async (textElement: HTMLElement | null, buttonId: string) => {
    try {
      const clipboardItem = textElement
        ? new ClipboardItem({
            'text/plain': new Blob([textElement.innerText], { type: 'text/plain' }),
            'text/html': new Blob([textElement.outerHTML], { type: 'text/html' }),
          })
        : new ClipboardItem({ 'text/plain': new Blob([''], { type: 'text/plain' }) });
      await clipboardWrite([clipboardItem]);

      const button = document.getElementById(buttonId);
      if (button) {
        const resetCopyText = () => {
          timeouts[buttonId] = setTimeout(() => {
            button.innerText = 'Copy';
          }, TIMEOUT_MS);
        };

        button.innerText = 'Copied!';
        if (timeouts[buttonId]) clearTimeout(timeouts[buttonId]);
        resetCopyText();
      }
    } catch (error) {
      dispatch(addNegativeToast('Failed to copy text to clipboard', { timeout: TIMEOUT_MS }));
    }
  };

  return (
    <DetailsContainer data-test="sms-smart-opt-in-details">
      <Header>
        <DetailsHeaderWrapper>
          <span data-test="sms-smart-opt-in-details-display-name">{selectedLink.name}</span>
          <StyledCaption data-test="sms-smart-opt-in-details-description">
            {selectedLink.description || strSmsSmartOptInDetailsDescriptionDefault}
          </StyledCaption>
        </DetailsHeaderWrapper>
        <Button
          onClick={() => setIsEditing(true)}
          variant={BUTTON_VARIANTS.SECONDARY}
          data-test="sms-smart-opt-in-edit-button"
        >
          <Edit16 />
          <DetailsHeaderEditLabel>{btnSmsSmartOptInDetailsEdit}</DetailsHeaderEditLabel>
        </Button>
      </Header>
      {shouldShowBanner && (
        <StyledNeutralBanner
          Icon={null}
          onClose={() => setShouldShowBanner(false)}
          title={strSmsSmartOptInDetailsHeaderNeutralBannerTitle}
        >
          {`${strSmsSmartOptInDetailsHeaderNeutralBannerBody} `}
          <NeutralLink href={strSmsSmartOptInDocumentationLinkUrl} target="_blank">
            {strSmsSmartOptInDetailsHeaderNeutralBannerLink}
          </NeutralLink>
        </StyledNeutralBanner>
      )}
      <DetailFieldsWrapper>
        <div data-test="sms-smart-opt-in-details-smart-link">
          <Label>{labelSmsSmartOptInDetailsLink}</Label>
          <CopyContent>
            <InfoText ref={smartLinkTextRef}>{selectedLink.optInUrl}</InfoText>
            <CopyButton
              id={COPY_LINK_BUTTON_ID}
              onClick={() => handleCopyToClipboard(smartLinkTextRef.current, COPY_LINK_BUTTON_ID)}
            >
              {btnSmsSmartOptInDetailsCopy}
            </CopyButton>
          </CopyContent>
        </div>
        <div data-test="sms-smart-opt-in-details-disclaimer">
          <Label>{labelSmsSmartOptInDetailsDisclaimer}</Label>
          <CopyContent>
            <div>
              <ExpandableContainer ref={disclaimerTextRef} isExpanded={shouldShowFullDisclaimer}>
                {getDisclaimerText()}
                {asLink(strSmsSmartOptInDetailsDisclaimerTermsText, selectedLink.termsLink)}
                {' or '}
                {asLink(strSmsSmartOptInDetailsDisclaimerPrivacyText, selectedLink.privacyLink)}
              </ExpandableContainer>
              <div>
                <InfoText
                  onClick={() => setShouldShowFullDisclaimer(!shouldShowFullDisclaimer)}
                  isUnderlined
                >
                  {shouldShowFullDisclaimer
                    ? strSmsSmartOptInDetailsDisclaimerHideInfoText
                    : strSmsSmartOptInDetailsDisclaimerShowInfoText}
                </InfoText>
              </div>
            </div>
            <CopyButton
              id={COPY_DISCLAIMER_BUTTON_ID}
              onClick={() =>
                handleCopyToClipboard(disclaimerTextRef.current, COPY_DISCLAIMER_BUTTON_ID)
              }
            >
              {btnSmsSmartOptInDetailsCopy}
            </CopyButton>
          </CopyContent>
        </div>
        <StyledFormField
          data-test="sms-smart-opt-in-details-brand-name"
          labelText={labelSmsSmartOptInFormBrandName}
          isHorizontalLayout
        >
          <span>{selectedLink.brandName}</span>
        </StyledFormField>
        <StyledFormField
          data-test="sms-smart-opt-in-details-message-type"
          labelText={labelSmsSmartOptInFormMessageType}
          isHorizontalLayout
        >
          <span>{messageType?.name}</span>
        </StyledFormField>
        <StyledFormField
          data-test="sms-smart-opt-in-details-sender"
          labelText={labelSmsSmartOptInFormSender}
          isHorizontalLayout
        >
          <span>{smsSender?.name}</span>
        </StyledFormField>
        <StyledFormField
          data-test="sms-smart-opt-in-details-terms-link"
          labelText={labelSmsSmartOptInFormTermsLink}
          isHorizontalLayout
        >
          <span>{selectedLink.termsLink || strUnsetText}</span>
        </StyledFormField>
        <StyledFormField
          data-test="sms-smart-opt-in-details-privacy-link"
          labelText={labelSmsSmartOptInFormPrivacyLink}
          isHorizontalLayout
        >
          <span>{selectedLink.privacyLink || strUnsetText}</span>
        </StyledFormField>
        <StyledFormField
          data-test="sms-smart-opt-in-details-redirect-link"
          labelText={labelSmsSmartOptInFormRedirectLink}
          isHorizontalLayout
        >
          <span>{selectedLink.fallbackUrl || strUnsetText}</span>
        </StyledFormField>
      </DetailFieldsWrapper>
      <SmsSmartOptInPreview />
    </DetailsContainer>
  );
};
